import {useState} from "react";
import classNames from "classnames";
import {ImageIcon} from "../atoms/icon";
import {formatDate, formatTime} from "../../lib/date-time"
import Image from "next/image";
import {absoluteURL} from "../../lib/absolute-url";
import {DrupalDocumentMedia} from "../../types/hygiena-types";
import {Button} from "../atoms/button";

/**
 * Displays an accordion.
 *
 * @param term
 *   The term to group by.
 * @param products
 *   The products.
 * @param gridKey
 *   A key parameter, to prevent filters from filtering other containers.
 * @param defaultOpen
 *   Whether to set the accordion open by default.
 * @constructor
 */

type ContentRows = {
  field_scientific_poster_location: string
  title: string
  field_scientific_posters_authors: string[]
  field_sp_start_time: string
  field_sp_end_time: string
  field_sp_document: DrupalDocumentMedia,
  field_sp_show_datetime: boolean,
}

interface WidgetAccordionProps {
  title: string
  content: ContentRows[]
}

export function WidgetAccordionRows ({ title, content }: WidgetAccordionProps) {
  const [active, setActive] = useState(false);

  /**
   * Create a simple triangle.
   *
   * @todo this is also used in key_value_page--support.tsx, let's refactor.
   *
   * @constructor
   */
  function Triangle({active}: {active: boolean}): JSX.Element {
    return <span className={classNames(
      "flex pr-4 transition-all duration-500 ease-in-out",
      {"transform rotate-90 -translate-x-5 translate-y-2.5": active},
    )}>
      <ImageIcon type="chevron-right" color="black" height={25} width={25} alt={"Expand or Collapse"} className="items-center max-h-[23px] mt-[5px]"/>
    </span>
  }

  return (
    <div>
      <div className="flex bg-white drop-shadow-sm cursor-pointer py-5" onClick={() => setActive(!active)}>
        <h3 className="px-8 text-left !font-light flex-grow">{title}</h3>
        <Triangle active={active} />
      </div>
      <div className={classNames(
        "overflow-hidden transition-all delay-200 ease-linear origin-top py-4",
        { "hidden": !active },
      )}>
        {content.map((item, i) => (
          <div key={`accordion-row--${title}--${item.title}--${i}`} className="container mx-auto text-left">
            <div className="flex flex-row bg-white p-6 mb-2 items-start shadow-lg">
              <Image src={`${absoluteURL("/sites/default/files/scientific-poster.webp", true)}`} width={60} height={45} alt="Scientific Poster" className="flex mr-6 items-top"/>
                <div className="flex-grow">
                  <div>
                    {item?.field_sp_show_datetime ? (
                      <p
                        className="text-sm text-primary font-bold pb-1 mb-0">{formatDate(item.field_sp_start_time)} | {formatTime(item.field_sp_start_time)}{item.field_sp_start_time != item.field_sp_end_time ? ` to ${formatTime(item.field_sp_end_time)}` : ''} {item.field_scientific_poster_location ? `| ${item.field_scientific_poster_location}` : ''}</p>
                    ): <>{item?.field_scientific_poster_location && (<p className="text-sm text-primary font-bold pb-1 mb-0">{item.field_scientific_poster_location}</p>)}</>}
                    <p className="text-lg text-black font-bold mb-0">{item.title}</p>
                    <small className="text-sm text-black">{item.field_scientific_posters_authors.join(', ')}</small>
                  </div>
                </div>
              {item?.field_sp_document?.field_media_file?.uri?.url && (
                <Button
                  icon="search"
                  iconClass="!mr-0"
                  height={25}
                  width={25}
                  color={"primary"} hover={{color: "primary"}}
                  className="flex-shrink rounded-lg py-3"
                  invert={true}
                  href={absoluteURL(item.field_sp_document.field_media_file.uri.url, true)}/>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}